import { defineStore } from 'pinia'
import type { IShiftActivity } from '@/services/campaign/interfaces/shift-activity.interface'
import type { IOptionsCampaign } from '@/services/campaign/interfaces/options-campaign.interface'
import type { IUpdateCampaign } from '@/services/campaign/interfaces/update-campaign.interface'
import type { IFormOption } from '@/services/form/interfaces/form-option.interface'
import type { ContactDto } from '@/services/contact/interfaces/contact.interface'
import { TypeCampaign } from '@/services/campaign/enums/type-campaign.enum'

type CampaignCallSettings = Pick<IOptionsCampaign | IUpdateCampaign, 'use_ai'> & {
  enmascarar?: boolean
  masked_number?: boolean
}

export const useWorkspaceStore = defineStore('workspace', {
  state: () => ({
    campaigns: [] as IOptionsCampaign[],
    shiftActivity: [] as IShiftActivity[],
    acknowledgedMissedCallsCount: 0,
    campaignUseIaById: {} as Record<string, boolean>,
    campaignMaskNumberById: {} as Record<string, boolean>,
    isCampaignsLoading: false as boolean,
    hasLoadedCampaigns: false as boolean,
    selectedCampaignId: null as string | null,
    selectedCampaign: null as IUpdateCampaign | null,
    campaignForms: [] as IFormOption[],
    campaignScript: '' as string,
    campaignName: '' as string,
    campaignType: '' as string,
    contactPhone: '' as string,
    contact: null as ContactDto | null,
    webphoneCallStatus: 'idle' as
      | 'idle'
      | 'dialing'
      | 'ringing'
      | 'calling'
      | 'incoming'
      | 'connected'
      | 'held'
      | 'ended',
    webphoneCallStatusLabel: '' as string,
    webphoneCallDuration: '00:00' as string,
    webphonePipExpanded: false as boolean,
    selectedFormId: null as string | null,
    selectedForm: null as IFormOption | null,
    hasPendingFormChanges: false,
    showNotesPanel: false as boolean,
    showCalendarPanel: false as boolean,
    useIa: false as boolean,
    callReturnRoute: null as string | null,
    callCampaignId: null as string | null,
    callUsesIa: false as boolean,
    callMaskNumber: false as boolean,
  }),
  getters: {
    missedCallsCount: (state) =>
      state.shiftActivity.reduce(
        (total, item) => total + Number(item.answered_and_missed_calls?.missed ?? 0),
        0,
      ),
    unreadMissedCallsCount(): number {
      return Math.max(0, this.missedCallsCount - this.acknowledgedMissedCallsCount)
    },
    isNotesPanelVisible: (state) => state.showNotesPanel,
    isCalendarPanelVisible: (state) => state.showCalendarPanel,
    isIaEnabled: (state) => state.useIa,
    getCampaignUseIa: (state) => (campaignId?: string | null) =>
      campaignId ? (state.campaignUseIaById[campaignId] ?? false) : false,
    getCampaignMaskNumber: (state) => (campaignId?: string | null) =>
      campaignId ? (state.campaignMaskNumberById[campaignId] ?? false) : false,
    hasVoiceCampaign: (state) =>
      state.campaigns.some(
        (campaign) =>
          campaign.type === TypeCampaign.OUTGOING || campaign.type === TypeCampaign.INCOMING,
      ),
    hasWhatsAppCampaign: (state) =>
      state.campaigns.some((campaign) => campaign.type === TypeCampaign.CHAT),
  },
  actions: {
    acknowledgeMissedCalls() {
      this.acknowledgedMissedCallsCount = this.missedCallsCount
    },
    setShiftActivity(activity: IShiftActivity[]) {
      this.shiftActivity = activity
      // A new shift can restart the server total from zero.
      this.acknowledgedMissedCallsCount = Math.min(
        this.acknowledgedMissedCallsCount,
        this.missedCallsCount,
      )
    },
    getCampaignUseAi(campaign: CampaignCallSettings) {
      return campaign.use_ai
    },
    getCampaignMask(campaign: CampaignCallSettings) {
      return campaign.masked_number === true || campaign.enmascarar === true
    },
    setCampaigns(campaigns: IOptionsCampaign[]) {
      this.campaigns = campaigns
      this.hasLoadedCampaigns = true
      this.campaignUseIaById = campaigns.reduce<Record<string, boolean>>((acc, campaign) => {
        acc[campaign.id] = this.getCampaignUseAi(campaign)
        return acc
      }, {})
      this.campaignMaskNumberById = campaigns.reduce<Record<string, boolean>>((acc, campaign) => {
        acc[campaign.id] = this.getCampaignMask(campaign)
        return acc
      }, {})
    },
    setCampaignsLoading(value: boolean) {
      this.isCampaignsLoading = value
    },
    selectCampaign(id: string) {
      this.selectedCampaignId = id
      this.useIa = this.campaignUseIaById[id] ?? false
      this.callMaskNumber = this.campaignMaskNumberById[id] ?? false
    },
    setSelectedCampaign(campaign: IUpdateCampaign | null) {
      if (!campaign) {
        this.selectedCampaign = null
        this.campaignName = ''
        this.campaignType = ''
        this.campaignScript = ''
        this.campaignForms = []
        this.useIa = false
        return
      }
      this.selectedCampaign = campaign
      this.campaignName = campaign.name
      this.campaignType = campaign.type
      this.campaignScript = campaign.content || ''
      this.campaignForms = campaign.forms || []
      this.setCampaignUseIa(campaign.id, this.getCampaignUseAi(campaign))
      this.setCampaignMaskNumber(campaign.id, this.getCampaignMask(campaign))
    },
    setSelectedForm(form: IFormOption | null) {
      this.selectedForm = form
      this.selectedFormId = form ? form.id : null
    },
    setContactPhone(phone: string) {
      this.contactPhone = phone
    },
    setContact(contact: ContactDto | null) {
      this.contact = contact
    },
    setWebphoneCallState(payload: {
      status:
        | 'idle'
        | 'dialing'
        | 'ringing'
        | 'calling'
        | 'incoming'
        | 'connected'
        | 'held'
        | 'ended'
      label?: string
      duration?: string
    }) {
      this.webphoneCallStatus = payload.status
      this.webphoneCallStatusLabel = payload.label ?? ''
      this.webphoneCallDuration = payload.duration ?? '00:00'
      if (payload.status === 'idle' || payload.status === 'ended') {
        this.webphonePipExpanded = false
      }
    },
    setWebphonePipExpanded(value: boolean) {
      this.webphonePipExpanded = value
    },
    toggleWebphonePipExpanded() {
      this.webphonePipExpanded = !this.webphonePipExpanded
    },
    toggleNotesPanel() {
      this.showNotesPanel = !this.showNotesPanel
      if (this.showNotesPanel) {
        this.showCalendarPanel = false
      }
    },
    toggleCalendarPanel() {
      this.showCalendarPanel = !this.showCalendarPanel
      if (this.showCalendarPanel) {
        this.showNotesPanel = false
      }
    },
    setUseIa(value: boolean) {
      this.useIa = value
    },
    setCampaignUseIa(campaignId: string, value: boolean) {
      this.campaignUseIaById = {
        ...this.campaignUseIaById,
        [campaignId]: value,
      }
      if (this.selectedCampaignId === campaignId) {
        this.useIa = value
      }
      if (this.callCampaignId === campaignId) {
        this.callUsesIa = value
        this.useIa = value
      }
    },
    setCampaignMaskNumber(campaignId: string, value: boolean) {
      this.campaignMaskNumberById = {
        ...this.campaignMaskNumberById,
        [campaignId]: value,
      }
      if (
        this.callCampaignId === campaignId ||
        (!this.callCampaignId && this.selectedCampaignId === campaignId)
      ) {
        this.callMaskNumber = value
      }
    },
    setCallMaskNumber(value: boolean) {
      this.callMaskNumber = value
    },
    toggleUseIa() {
      this.useIa = !this.useIa
    },
    setCallNavigationContext(payload: {
      campaignId: string
      returnRoute?: string | null
      useIa: boolean
      maskNumber: boolean
    }) {
      this.callCampaignId = payload.campaignId
      this.callReturnRoute = payload.returnRoute ?? null
      this.callUsesIa = payload.useIa
      this.callMaskNumber = payload.maskNumber
    },
    startDirectCallNavigation(returnRoute: string) {
      this.selectedCampaignId = null
      this.setSelectedCampaign(null)
      this.callCampaignId = null
      this.callReturnRoute = returnRoute
      this.callUsesIa = false
      this.callMaskNumber = false
    },
    clearCallNavigationContext() {
      this.callCampaignId = null
      this.callReturnRoute = null
      this.callUsesIa = false
      this.callMaskNumber = false
    },
  },
})
